import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { GeofenceEnforcedAction } from 'app/API.service.custom';
import { DialogConfig, DialogRef } from 'app/shared/dialog';
import { ToastrService } from 'ngx-toastr';
import { GeofenceAdminService, parseErrorCode } from '../geofence-admin.service';
import { GeofenceMapComponent } from '../geofence-map/geofence-map.component';
import { geometryToAreas } from '../geofence-geometry';
import {
  GEOFENCE_ENFORCED_ACTION_OPTIONS,
  Geofence,
  GeofenceArea,
  GeofenceMember,
} from '../geofence.model';

export interface GeofenceAssignmentPanelData {
  member: GeofenceMember;
  /** ACTIVE geofences on the account, already loaded by the caller. */
  geofences: Geofence[];
  accountId: string;
}

/**
 * Attach or detach ONE member's geofence and choose which actions it gates.
 *
 * Two rules drive the whole screen:
 *
 *  1. One geofence per member. `operatingGeofenceId` is a single ID, so this is
 *     a radio choice, never a multi-select. Needing a second region means adding
 *     an area to the assigned geofence, not attaching another geofence.
 *  2. Fail-closed. Enforced actions with no geofence are DENIED outright, so
 *     detaching offers to clear them in the same write and the combination is
 *     called out before it can be saved.
 */
@Component({
  selector: 'app-geofence-assignment-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, GeofenceMapComponent],
  templateUrl: './geofence-assignment-panel.component.html',
  styleUrls: ['./geofence-assignment-panel.component.scss'],
})
export class GeofenceAssignmentPanelComponent implements OnInit {
  readonly actionOptions = GEOFENCE_ENFORCED_ACTION_OPTIONS;

  member!: GeofenceMember;
  geofences: Geofence[] = [];
  accountId = '';

  selectedGeofenceId: string | null = null;
  selectedActions = new Set<GeofenceEnforcedAction>();
  previewAreas: GeofenceArea[] = [];
  saving = false;

  /** Logical id the panel opened on, so `hasChanges` compares like with like. */
  private originalGeofenceId: string | null = null;

  constructor(
    private config: DialogConfig<GeofenceAssignmentPanelData>,
    private dialogRef: DialogRef,
    private geofenceAdmin: GeofenceAdminService,
    private toastr: ToastrService,
  ) {}

  ngOnInit(): void {
    const data = this.config.data;
    this.member = data.member;
    this.geofences = data.geofences;
    this.accountId = data.accountId;

    // `operatingGeofenceId` holds a REVISION id; the picker and the write both
    // work in logical ids, so resolve it to the geofence it belongs to.
    this.selectedGeofenceId =
      this.geofences.find(g => this.geofenceAdmin.matchesMember(g, this.member))?.logicalGeofenceId || null;
    this.originalGeofenceId = this.selectedGeofenceId;
    this.selectedActions = new Set(this.member.geofenceEnforcedActions);
    this.updatePreview();
  }

  // ------------------------------------------------------------- selection

  selectGeofence(geofenceId: string | null): void {
    this.selectedGeofenceId = geofenceId;
    this.updatePreview();
  }

  toggleAction(action: GeofenceEnforcedAction): void {
    if (this.selectedActions.has(action)) this.selectedActions.delete(action);
    else this.selectedActions.add(action);
  }

  isActionSelected(action: GeofenceEnforcedAction): boolean {
    return this.selectedActions.has(action);
  }

  clearAllActions(): void {
    this.selectedActions.clear();
  }

  private updatePreview(): void {
    this.previewAreas = this.selectedGeofence ? geometryToAreas(this.selectedGeofence.geometry) : [];
  }

  get selectedGeofence(): Geofence | undefined {
    return this.geofences.find(g => g.logicalGeofenceId === this.selectedGeofenceId);
  }

  /**
   * The fail-closed combination: actions to enforce, but nothing to enforce them
   * against. Every one of those actions is denied for this member.
   */
  get isFailClosed(): boolean {
    return !this.selectedGeofenceId && this.selectedActions.size > 0;
  }

  get isRemovingExistingGeofence(): boolean {
    return !!this.member.operatingGeofenceId && !this.selectedGeofenceId;
  }

  get hasChanges(): boolean {
    const before = [...this.member.geofenceEnforcedActions].sort().join(',');
    const after = [...this.selectedActions].sort().join(',');
    return this.originalGeofenceId !== this.selectedGeofenceId || before !== after;
  }

  get isSaveDisabled(): boolean {
    return this.saving || !this.hasChanges || this.isFailClosed;
  }

  // ------------------------------------------------------------------ save

  async save(): Promise<void> {
    if (this.isSaveDisabled) return;
    this.saving = true;

    const actions = [...this.selectedActions];
    try {
      if (this.selectedGeofenceId) {
        await this.geofenceAdmin.assignMember({
          accountId: this.accountId,
          membershipId: this.member.membershipId,
          logicalGeofenceId: this.selectedGeofenceId,
          enforcedActions: actions,
        });
        const name = this.selectedGeofence?.name || 'the geofence';
        this.toastr.success(
          actions.length
            ? `${this.member.name} is on ${name}, enforcing ${actions.length} action${actions.length === 1 ? '' : 's'}.`
            : `${this.member.name} is on ${name}. No actions are enforced yet.`,
          'Geofence assigned',
        );
      } else {
        // The server clears enforced actions as part of this call, so a member can
        // never be left with actions and no geofence to check them against.
        await this.geofenceAdmin.unassignMember(this.accountId, this.member.membershipId);
        this.toastr.success(
          `${this.member.name} has no geofence. Attendance and visits are no longer location-restricted.`,
          'Geofence removed',
        );
      }

      this.dialogRef.close({
        membershipId: this.member.membershipId,
        // Report back the ACTIVE revision id, matching what a reload would read.
        operatingGeofenceId: this.selectedGeofence?.id ?? null,
        geofenceEnforcedActions: this.selectedGeofenceId ? actions : [],
      });
    } catch (error: any) {
      console.error('Geofence assignment failed', error);
      this.toastr.error(this.errorMessage(error), `Could not update ${this.member.name}`);
      this.saving = false;
    }
  }

  private errorMessage(error: any): string {
    const { code, message } = parseErrorCode(error);
    if (code === 'UNAUTHORIZED' || code === 'FORBIDDEN') {
      return 'Only the account owner can change geofence assignments.';
    }
    return message || 'The server rejected the change. Nothing was saved — try again.';
  }

  close(): void {
    this.dialogRef.close(null);
  }
}
